import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../components/layout/DashboardShell";
import PublicNavbar from "../components/layout/PublicNavbar";
import { Card, Pill, Button, Select, Input, TextArea } from "../components/common/Primitives";
import TrustBadge from "../components/marketplace/TrustBadge";
import ClosingChecklistWidget from "../components/deal_room/ClosingChecklistWidget";
import { FileText, MessageSquare, DoorOpen, Download, CheckCircle, PenLine, ShieldCheck, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatDate } from "../lib/datetime";
import { useAuth } from "../lib/AuthContext";

const STAGES = [
  "Exploring", "Reviewing", "Communication", "NDA Signed",
  "LOI Issued", "Term Sheet", "Definitive Docs", "Closed", "Dropped",
];

const STAGE_DOC_TYPES = [
  { key: "nda", label: "NDA", stageMatch: "NDA Signed" },
  { key: "loi", label: "Letter of Intent (LOI)", stageMatch: "LOI Issued" },
  { key: "term_sheet", label: "Term Sheet", stageMatch: "Term Sheet" },
  { key: "definitive", label: "Definitive Documents", stageMatch: "Definitive Docs" },
  { key: "diligence_report", label: "Diligence Report", stageMatch: null },
];

// The backend bakes an absolute signer URL using its FRONTEND_URL env var.
// If that env is misconfigured on a deployment (e.g. still points to
// http://localhost:3000), the "Sign now" button would open a dead link.
// Rewrite localhost/relative signer URLs to the browser's current origin so
// clicks always land on the correct deployed UI.
function _resolveSignerUrl(url) {
  if (!url) return null;
  try {
    // Also treat any placeholder.venturaz.com URL as bad (legacy mock provider
    // baked absolute URLs pointing at a domain we never owned).
    if (url.includes("placeholder.venturaz.com")) return null;
    if (url.startsWith("/")) {
      return `${window.location.origin}${url}`;
    }
    const u = new URL(url);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") {
      return `${window.location.origin}${u.pathname}${u.search}${u.hash}`;
    }
    return url;
  } catch {
    return url;
  }
}

export default function DealRoomDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [stage, setStage] = useState("");
  const [closing, setClosing] = useState(false);
  const [closeForm, setCloseForm] = useState({ committed_amount_inr: "", success_fee_pct: "2.0", billed_to_email: "" });

  const load = () => api.get(`/deal-room/${id}`).then((r) => { setData(r.data); setStage(r.data?.room?.stage || ""); }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [id]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/deal-room/${id}/note`, { text: note });
      toast.success("Note added.");
      setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStage = async () => {
    try {
      await api.put(`/deal-room/${id}`, { stage });
      toast.success("Stage updated.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeDeal = async () => {
    const amt = parseFloat(closeForm.committed_amount_inr);
    const pct = parseFloat(closeForm.success_fee_pct);
    if (!amt || amt <= 0) return toast.error("Enter committed amount in INR.");
    if (!pct || pct <= 0 || pct > 20) return toast.error("Success fee % must be between 0 and 20.");
    try {
      const { data: resp } = await api.post(`/deal-room/${id}/close`, {
        committed_amount_inr: amt,
        success_fee_pct: pct,
        billed_to_email: closeForm.billed_to_email || undefined,
      });
      toast.success(`Deal closed. Invoice ${resp.invoice.invoice_id} generated.`);
      setClosing(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadInvoice = async () => {
    try {
      const resp = await api.get(`/deal-room/${id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.room?.invoice_id || "invoice"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("doc_type", docType);
    form.append("file", file);
    try {
      await api.post(`/deal-room/${id}/upload-doc`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${docType.toUpperCase()} uploaded`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const resp = await api.get(`/deal-room/${id}/documents/${docId}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadSignedDoc = async (docId, filename) => {
    // Signed PDF from the native e-sign envelope — includes the audit-trail
    // certificate page + SHA-256 integrity chain. Only available once both
    // parties have signed (backend returns 404 otherwise).
    try {
      const resp = await api.get(`/deal-room/${id}/documents/${docId}/signed`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (filename || `${docId}.pdf`).replace(/\.pdf$/i, ".signed.pdf");
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteDoc = async (docId, filename) => {
    if (!window.confirm(`Delete "${filename}"?\n\nThis removes the file from the deal room. Signed docs cannot be deleted.`)) return;
    try {
      await api.delete(`/deal-room/${id}/documents/${docId}`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const replaceDoc = async (docId, file, needsApproval) => {
    if (file.size > 25 * 1024 * 1024) { toast.error("File exceeds 25 MB"); return; }
    if (needsApproval) {
      const reason = window.prompt("This is the 3rd+ change to this diligence document — an admin has to approve. Why the update?", "");
      if (reason === null) return;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("reason", reason || "");
      try {
        const { data } = await api.post(`/deal-room/${id}/documents/${docId}/replace`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data.status === "pending_admin_approval") {
          toast.info(data.message || "Submitted to admin for approval.");
        } else {
          toast.success("Document replaced.");
        }
        load();
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/deal-room/${id}/documents/${docId}/replace`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document replaced.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendForSignature = async (docId) => {
    try {
      const { data: resp } = await api.post(`/deal-room/${id}/esign/send`, { doc_id: docId });
      if (resp.mock) {
        toast.success("E-sign request created (legacy mock). Signing links generated for both parties.");
      } else {
        toast.success("Sent for signature. Both parties emailed with their unique signing links.");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [genForm, setGenForm] = useState({
    open: null, amount_inr: "", valuation_inr: "", deadline_days: "",
    equity_stake_pct: "", liquidation_pref: "1x non-participating",
    board_seat: true, anti_dilution: "Broad-based weighted average",
    additional_terms: "",
    // NDA-specific fields — every party can edit these before generation.
    nda_purpose: "",
    nda_jurisdiction_city: "Bengaluru",
    nda_term_years: 3,
    nda_custom_clauses: "",
  });
  const generateTemplate = async (docType) => {
    const amount = parseFloat(genForm.amount_inr);
    const valuation = parseFloat(genForm.valuation_inr);
    const deadline = genForm.deadline_days ? parseInt(genForm.deadline_days, 10) : undefined;
    const stake = parseFloat(genForm.equity_stake_pct);
    try {
      const body = {};
      if (docType === "nda") {
        body.nda_purpose = genForm.nda_purpose || undefined;
        body.nda_jurisdiction_city = genForm.nda_jurisdiction_city || undefined;
        body.nda_term_years = genForm.nda_term_years || undefined;
        body.nda_custom_clauses = genForm.nda_custom_clauses || undefined;
      } else {
        body.amount_inr = isNaN(amount) ? undefined : amount;
        body.valuation_inr = isNaN(valuation) ? undefined : valuation;
        body.deadline_days = deadline;
        body.additional_terms = genForm.additional_terms || undefined;
        if (docType === "term_sheet") {
          body.equity_stake_pct = isNaN(stake) ? undefined : stake;
          body.liquidation_pref = genForm.liquidation_pref || undefined;
          body.board_seat = !!genForm.board_seat;
          body.anti_dilution = genForm.anti_dilution || undefined;
        }
      }
      const { data: resp } = await api.post(`/deal-room/${id}/generate/${docType}`, body);
      toast.success(`${resp.label} generated and attached to the deal room.`);
      setGenForm({
        open: null, amount_inr: "", valuation_inr: "", deadline_days: "",
        equity_stake_pct: "", liquidation_pref: "1x non-participating",
        board_seat: true, anti_dilution: "Broad-based weighted average",
        additional_terms: "",
        nda_purpose: "", nda_jurisdiction_city: "Bengaluru", nda_term_years: 3,
        nda_custom_clauses: "",
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const simulateSigned = async (docId) => {
    try {
      await api.post(`/deal-room/${id}/esign/simulate`, { doc_id: docId });
      toast.success("Signature ceremony completed (simulated).");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ESIGNABLE = new Set(["nda", "loi", "term_sheet", "definitive"]);

  const isAdmin = ["super_admin", "admin", "manager", "agent"].includes(user?.role);

  if (!data) return (
    <div className="min-h-screen bg-transparent text-ink">
      <PublicNavbar />
      <div className="max-w-5xl mx-auto px-6 py-16 text-ink-muted">Loading…</div>
    </div>
  );

  const content = (
    <>
      <Toaster richColors position="top-right" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card testId="dr-parties">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="overline">Startup</div>
                <div className="font-heading text-ink text-xl mt-1">{data.startup?.startup_name}</div>
                <div className="text-xs text-ink-muted font-mono uppercase tracking-wider">{data.startup?.sector} · {data.startup?.business_stage} · {data.startup?.city}</div>
                {data.trust_score !== null && data.trust_score !== undefined && (
                  <div className="mt-2" data-testid="dr-startup-trust">
                    <TrustBadge total={data.trust_score} label={data.trust_label} />
                  </div>
                )}
              </div>
              <div>
                <div className="overline">Investor</div>
                <div className="font-heading text-ink text-xl mt-1">{data.investor?.investor_name || data.investor?.firm_name}</div>
                <div className="text-xs text-ink-muted font-mono uppercase tracking-wider">{data.investor?.investor_type} · {data.investor?.city}</div>
              </div>
            </div>
          </Card>

          <Card testId="dr-funding">
            <div className="overline mb-3">Deal Summary</div>
            <div className="text-ink-soft text-sm mb-4">{data.room?.deal_summary || "—"}</div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="overline">Ask</div>
                <div className="font-mono text-ink mt-1">₹{((data.funding?.amount_required || 0) / 1e7).toFixed(2)} Cr</div>
              </div>
              <div>
                <div className="overline">Type</div>
                <div className="font-mono text-ink mt-1">{data.funding?.funding_type || "—"}</div>
              </div>
              <div>
                <div className="overline">Valuation</div>
                <div className="font-mono text-ink mt-1">₹{((data.funding?.valuation_expectation || 0) / 1e7).toFixed(1)} Cr</div>
              </div>
            </div>
          </Card>

          <Card testId="dr-documents">
            <div className="overline mb-3 flex items-center gap-2"><FileText size={14} /> Deal Documents</div>
            <div className="text-xs text-ink-muted mb-4">Upload signed PDFs at each stage. Only the two deal parties and admin can access these.</div>
            <div className="space-y-4">
              {STAGE_DOC_TYPES.map((t) => {
                const docs = (data.room?.documents || []).filter((d) => d.doc_type === t.key);
                return (
                  <div key={t.key} className="border border-navy/10 rounded-md p-4" data-testid={`dr-docsec-${t.key}`}>
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                      <div>
                        <div className="text-ink font-heading text-sm">{t.label}</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{docs.length} file{docs.length === 1 ? "" : "s"}</div>
                      </div>
                      {(() => {
                        // Author permissions — NDA is open to both parties;
                        // LOI, Term Sheet and Definitive Docs are the investor's
                        // instrument (backend enforces the same rule). Admin
                        // sees everything.
                        const isInvestorOnly = t.key === "loi" || t.key === "term_sheet" || t.key === "definitive";
                        const canAuthor = !isInvestorOnly || user?.role === "investor" || (user?.role || "").includes("admin");
                        if (!canAuthor) {
                          return (
                            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted italic" data-testid={`dr-author-locked-${t.key}`}>
                              Investor to propose
                            </div>
                          );
                        }
                        return (
                          <div className="flex items-center gap-2">
                            {(t.key === "loi" || t.key === "term_sheet" || t.key === "nda") && (
                              <button
                                type="button"
                                onClick={() => setGenForm({ ...genForm, open: t.key, amount_inr: "", valuation_inr: "", deadline_days: "" })}
                                className="text-xs font-heading text-[#02BB8E] hover:bg-[#02BB8E]/10 rounded px-3 py-1.5 border border-[#02BB8E]/40"
                                data-testid={`dr-generate-${t.key}`}
                              >
                                <Sparkles size={11} className="inline mr-1" /> Generate draft
                              </button>
                            )}
                            <label className="cursor-pointer text-xs font-heading text-[#8A6600] hover:bg-gold/10 rounded px-3 py-1.5 border border-gold/40" data-testid={`dr-upload-${t.key}`}>
                              <input
                                type="file"
                                className="hidden"
                                accept="application/pdf,.pdf"
                                onChange={(e) => e.target.files?.[0] && uploadDoc(t.key, e.target.files[0])}
                              />
                              + Upload PDF
                            </label>
                          </div>
                        );
                      })()}
                    </div>
                    {genForm.open === t.key && (
                      <div className="border border-[#02BB8E]/40 bg-[#02BB8E]/[0.06] rounded-md p-3 mb-3 space-y-2" data-testid={`dr-generate-form-${t.key}`}>
                        <div className="overline">Auto-draft {t.label}</div>
                        {t.key === "nda" ? (
                          <>
                            <p className="text-[11px] text-ink-muted leading-relaxed">
                              VenturaZ will draft a mutual NDA between both parties using our fixed template.
                              Either side may edit the purpose, jurisdiction, term, or add custom clauses below
                              before generating the PDF.
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                label="Jurisdiction city"
                                value={genForm.nda_jurisdiction_city}
                                onChange={(e) => setGenForm({ ...genForm, nda_jurisdiction_city: e.target.value })}
                                testId="dr-generate-nda-city"
                                placeholder="Bengaluru"
                              />
                              <Input
                                label="Term (years)"
                                type="number"
                                min="1" max="10"
                                value={genForm.nda_term_years}
                                onChange={(e) => setGenForm({ ...genForm, nda_term_years: parseInt(e.target.value, 10) || 3 })}
                                testId="dr-generate-nda-term"
                                placeholder="3"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-heading uppercase tracking-widest text-ink-muted mb-1 block">
                                Purpose of disclosure (optional)
                              </label>
                              <textarea
                                rows={2}
                                value={genForm.nda_purpose}
                                onChange={(e) => setGenForm({ ...genForm, nda_purpose: e.target.value })}
                                className="w-full border border-navy/15 rounded-md px-2 py-1.5 text-sm bg-white"
                                placeholder="e.g. evaluating a Series-A investment through the VenturaZ Deal Room."
                                data-testid="dr-generate-nda-purpose"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-heading uppercase tracking-widest text-ink-muted mb-1 block">
                                Custom clauses (optional, appended to standard NDA)
                              </label>
                              <textarea
                                rows={4}
                                value={genForm.nda_custom_clauses}
                                onChange={(e) => setGenForm({ ...genForm, nda_custom_clauses: e.target.value })}
                                className="w-full border border-navy/15 rounded-md px-2 py-1.5 text-sm bg-white font-mono"
                                placeholder={"e.g. 12. Data localisation — All shared data shall remain within Indian jurisdiction for the term of this Agreement.\n\n13. Return / destruction — Upon termination, all copies of Confidential Information shall be returned or destroyed within 15 days."}
                                data-testid="dr-generate-nda-clauses"
                              />
                              <div className="text-[10px] text-ink-muted mt-1 italic">
                                Both parties can add or edit these clauses before signature. Standard NDA clauses (confidentiality, non-use, jurisdiction, survival) are always included.
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-[11px] text-ink-muted leading-relaxed">VenturaZ will generate a branded {t.label} PDF pre-filled with the deal room&apos;s parties and defaults from the funding profile. Override amount / valuation / deadline below (optional).</p>
                            <div className="grid grid-cols-3 gap-2">
                              <Input label="Amount (INR)" type="number" value={genForm.amount_inr} onChange={(e) => setGenForm({ ...genForm, amount_inr: e.target.value })} testId={`dr-generate-amount-${t.key}`} placeholder={`${(data.funding?.amount_required || 0).toLocaleString("en-IN")}`} />
                              <Input label="Valuation (INR)" type="number" value={genForm.valuation_inr} onChange={(e) => setGenForm({ ...genForm, valuation_inr: e.target.value })} testId={`dr-generate-valuation-${t.key}`} placeholder={`${(data.funding?.valuation_expectation || 0).toLocaleString("en-IN")}`} />
                              <Input label={t.key === "loi" ? "Exclusivity days" : "Closing days"} type="number" value={genForm.deadline_days} onChange={(e) => setGenForm({ ...genForm, deadline_days: e.target.value })} testId={`dr-generate-deadline-${t.key}`} placeholder={t.key === "loi" ? "45" : "60"} />
                            </div>
                        {t.key === "term_sheet" && (
                          <div className="border-t border-[#02BB8E]/30 pt-2 mt-2 space-y-2">
                            <div className="overline text-[10px]">Rich Term Sheet Options</div>
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                label="Equity Stake % (post-money) — leave blank to auto-compute"
                                type="number" step="0.01"
                                value={genForm.equity_stake_pct}
                                onChange={(e) => setGenForm({ ...genForm, equity_stake_pct: e.target.value })}
                                testId="dr-generate-stake" placeholder="Auto"
                              />
                              <div>
                                <label className="text-[10px] font-heading uppercase tracking-widest text-ink-muted mb-1 block">Liquidation Preference</label>
                                <select
                                  value={genForm.liquidation_pref}
                                  onChange={(e) => setGenForm({ ...genForm, liquidation_pref: e.target.value })}
                                  className="w-full border border-navy/15 rounded-md px-2 py-1.5 text-sm bg-white"
                                  data-testid="dr-generate-liq-pref"
                                >
                                  <option value="1x non-participating">1x non-participating</option>
                                  <option value="1x participating">1x participating</option>
                                  <option value="1x participating with cap (3x)">1x participating with cap (3x)</option>
                                  <option value="2x non-participating">2x non-participating</option>
                                  <option value="2x participating">2x participating</option>
                                  <option value="No liquidation preference">No liquidation preference</option>
                                </select>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] font-heading uppercase tracking-widest text-ink-muted mb-1 block">Anti-Dilution</label>
                                <select
                                  value={genForm.anti_dilution}
                                  onChange={(e) => setGenForm({ ...genForm, anti_dilution: e.target.value })}
                                  className="w-full border border-navy/15 rounded-md px-2 py-1.5 text-sm bg-white"
                                  data-testid="dr-generate-antidil"
                                >
                                  <option value="Broad-based weighted average">Broad-based weighted average</option>
                                  <option value="Narrow-based weighted average">Narrow-based weighted average</option>
                                  <option value="Full ratchet">Full ratchet</option>
                                  <option value="None">None</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] font-heading uppercase tracking-widest text-ink-muted mb-1 block">Board Rights</label>
                                <select
                                  value={genForm.board_seat ? "yes" : "no"}
                                  onChange={(e) => setGenForm({ ...genForm, board_seat: e.target.value === "yes" })}
                                  className="w-full border border-navy/15 rounded-md px-2 py-1.5 text-sm bg-white"
                                  data-testid="dr-generate-board"
                                >
                                  <option value="yes">1 investor-nominated director</option>
                                  <option value="no">Observer rights only</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="text-[10px] font-heading uppercase tracking-widest text-ink-muted mb-1 block">Additional Terms (optional)</label>
                          <textarea
                            rows={2}
                            value={genForm.additional_terms}
                            onChange={(e) => setGenForm({ ...genForm, additional_terms: e.target.value })}
                            className="w-full border border-navy/15 rounded-md px-2 py-1.5 text-sm bg-white"
                            placeholder="Any additional term-specific clauses to include…"
                            data-testid={`dr-generate-additional-${t.key}`}
                          />
                        </div>
                          </>
                        )}
                        <div className="flex gap-2 pt-1">
                          <Button variant="gold" onClick={() => generateTemplate(t.key)} data-testid={`dr-generate-confirm-${t.key}`}>Generate PDF</Button>
                          <Button variant="ghost" onClick={() => setGenForm({ ...genForm, open: null })} data-testid={`dr-generate-cancel-${t.key}`}>Cancel</Button>
                        </div>
                      </div>
                    )}
                    {docs.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {docs.map((d) => {
                          const canEsign = ESIGNABLE.has(d.doc_type);
                          const esignStatus = d.esign_status; // undefined | sent | viewed | signed | declined
                          const esignProvider = d.esign_provider; // "native" | "mock" (legacy) | undefined
                          // Legacy mock envelopes (created before the native e-sign engine)
                          // must NEVER surface their placeholder.venturaz.com URLs.
                          // We treat them as "resend needed" and hide the sign links.
                          const isLegacyMockPlaceholder = esignProvider === "mock" && esignStatus === "PLACEHOLDER";
                          const isNativeMock = false; // deprecated — every new envelope is native
                          const isInvestor = (user?.role || "") === "investor";
                          const isStartupUser = (user?.role || "") === "startup";
                          const investorSignUrl = isLegacyMockPlaceholder ? null : _resolveSignerUrl(d.esign_signer_urls?.investor);
                          const startupSignUrl = isLegacyMockPlaceholder ? null : _resolveSignerUrl(d.esign_signer_urls?.startup);
                          const isMine = user?.user_id === d.uploaded_by;
                          const isImmutable = ["sent", "PLACEHOLDER", "viewed", "signed", "partial"].includes(esignStatus);
                          const canEditOrDelete = (isMine || isAdmin) && !isImmutable;
                          const changeCount = d.change_count || 0;
                          const nextChangeNeedsApproval = !canEsign && !isAdmin && changeCount >= 2;
                          return (
                            <div key={d.doc_id} className="rounded border border-navy/10 bg-white/40 p-2" data-testid={`dr-doc-${d.doc_id}`}>
                              <div className="flex items-center justify-between text-xs">
                                <div className="min-w-0 flex-1 truncate">
                                  <span className="text-ink font-mono">{d.filename}</span>
                                  <span className="text-ink-muted ml-2">· {(d.size / 1024).toFixed(1)} KB · by {d.uploaded_by_email}</span>
                                  {changeCount > 0 && (
                                    <span className="text-[#8A6600] ml-2 font-mono text-[10px]" title={`This file has been replaced ${changeCount} time${changeCount === 1 ? "" : "s"}`}>
                                      · v{changeCount + 1}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3">
                                  {esignStatus === "signed" && (
                                    <button
                                      onClick={() => downloadSignedDoc(d.doc_id, d.filename)}
                                      className="text-emerald-700 hover:underline font-heading"
                                      data-testid={`dr-download-signed-${d.doc_id}`}
                                      title="Signed PDF with audit-trail certificate"
                                    >Download signed</button>
                                  )}
                                  <button onClick={() => downloadDoc(d.doc_id, d.filename)} className="text-[#8A6600] hover:underline" data-testid={`dr-download-doc-${d.doc_id}`}>Download</button>
                                  {canEditOrDelete && (
                                    <>
                                      <label
                                        className="text-navy hover:text-[#0A1C40] cursor-pointer"
                                        data-testid={`dr-replace-doc-${d.doc_id}`}
                                        title={nextChangeNeedsApproval ? "Two prior changes on file — this replacement needs admin approval" : "Upload a new version"}
                                      >
                                        <input
                                          type="file"
                                          className="hidden"
                                          accept="application/pdf,.pdf"
                                          onChange={(e) => e.target.files?.[0] && replaceDoc(d.doc_id, e.target.files[0], nextChangeNeedsApproval)}
                                        />
                                        {nextChangeNeedsApproval ? "Replace (needs approval)" : "Replace"}
                                      </label>
                                      <button
                                        onClick={() => deleteDoc(d.doc_id, d.filename)}
                                        className="text-rose-700 hover:underline"
                                        data-testid={`dr-delete-doc-${d.doc_id}`}
                                        title="Delete this document"
                                      >Delete</button>
                                    </>
                                  )}
                                </div>
                              </div>
                              {canEsign && (
                                <div className="flex items-center justify-between mt-2 gap-2">
                                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
                                    {esignStatus === "signed" ? (
                                      <Pill tone="success" data-testid={`dr-esign-status-${d.doc_id}`}><ShieldCheck size={10} className="inline mr-1" /> Signed by both</Pill>
                                    ) : isLegacyMockPlaceholder ? (
                                      <Pill tone="warn" data-testid={`dr-esign-status-${d.doc_id}`}><PenLine size={10} className="inline mr-1" /> Legacy envelope · resend</Pill>
                                    ) : esignStatus === "sent" || esignStatus === "PLACEHOLDER" ? (
                                      <Pill tone="warn" data-testid={`dr-esign-status-${d.doc_id}`}><PenLine size={10} className="inline mr-1" /> Awaiting signatures</Pill>
                                    ) : esignStatus === "declined" ? (
                                      <Pill tone="danger" data-testid={`dr-esign-status-${d.doc_id}`}>Declined</Pill>
                                    ) : (
                                      <Pill tone="muted" data-testid={`dr-esign-status-${d.doc_id}`}>Not sent</Pill>
                                    )}
                                    {d.esign_sent_at && esignStatus !== "signed" && (
                                      <span className="text-ink-muted">· sent {formatDate(d.esign_sent_at)}</span>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    {(!esignStatus || isLegacyMockPlaceholder) && (
                                      <button
                                        onClick={() => sendForSignature(d.doc_id)}
                                        className="text-[11px] font-heading text-[#1F5B4A] hover:bg-[#1F5B4A]/10 rounded px-2 py-1 border border-[#1F5B4A]/40"
                                        data-testid={`dr-esign-send-${d.doc_id}`}
                                      >
                                        <PenLine size={11} className="inline mr-1" /> {isLegacyMockPlaceholder ? "Resend for signature" : "Send for signature"}
                                      </button>
                                    )}
                                    {isNativeMock && esignStatus && esignStatus !== "signed" && isAdmin && (
                                      <button
                                        onClick={() => simulateSigned(d.doc_id)}
                                        className="text-[11px] font-heading text-[#02BB8E] hover:bg-[#02BB8E]/10 rounded px-2 py-1 border border-[#02BB8E]/40"
                                        data-testid={`dr-esign-simulate-${d.doc_id}`}
                                        title="Admin only — legacy mock envelope completion"
                                      >
                                        <CheckCircle size={11} className="inline mr-1" /> Mark signed (legacy mock)
                                      </button>
                                    )}
                                    {/* Party-specific sign link — only show YOUR link if you're a party.
                                        Admins see both. This avoids the earlier bug where a startup could
                                        open the investor's signing link (which then rejected them anyway). */}
                                    {esignStatus && esignStatus !== "signed" && investorSignUrl && (isInvestor || isAdmin) && (
                                      <a
                                        href={investorSignUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[11px] font-heading text-[#8A6600] hover:bg-gold/10 rounded px-2 py-1 border border-gold/40"
                                        data-testid={`dr-esign-investor-link-${d.doc_id}`}
                                      ><PenLine size={11} className="inline mr-1" /> {isInvestor ? "Sign now →" : "Investor link ↗"}</a>
                                    )}
                                    {esignStatus && esignStatus !== "signed" && startupSignUrl && (isStartupUser || isAdmin) && (
                                      <a
                                        href={startupSignUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[11px] font-heading text-[#8A6600] hover:bg-gold/10 rounded px-2 py-1 border border-gold/40"
                                        data-testid={`dr-esign-startup-link-${d.doc_id}`}
                                      ><PenLine size={11} className="inline mr-1" /> {isStartupUser ? "Sign now →" : "Startup link ↗"}</a>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card testId="dr-queries">
            <div className="overline mb-3 flex items-center gap-2"><MessageSquare size={14} /> Query History</div>
            {(data.queries || []).length === 0 ? (
              <div className="text-ink-muted text-sm">No queries yet in this deal.</div>
            ) : (
              <div className="space-y-3">
                {data.queries.map((q) => (
                  <div key={q.query_id} className="border border-white/10 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs text-ink-muted font-mono">{q.query_id}</div>
                      <Pill tone={q.status === "Response Shared" ? "success" : "warn"}>{q.status}</Pill>
                    </div>
                    <ul className="space-y-1 mb-2">
                      {(q.questions || []).map((qq) => <li key={qq.question_id} className="text-ink-soft text-sm">• {qq.question}</li>)}
                    </ul>
                    {q.answer && <div className="text-ink-soft text-sm italic border-l-2 border-gold/50 pl-3 mt-2">{q.answer}</div>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card testId="dr-stage-timeline">
            <div className="overline mb-3 flex items-center gap-2"><DoorOpen size={14} /> Stage</div>
            <div className="text-2xl font-heading text-gold mb-4">{data.room?.stage}</div>
            {isAdmin && (
              <>
                <Select value={stage} onChange={(e) => setStage(e.target.value)} testId="dr-stage-select">
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
                <Button onClick={updateStage} className="mt-3 w-full" data-testid="dr-stage-update">Update Stage</Button>
              </>
            )}
            <div className="mt-6 space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-ink-muted">NDA</span><Pill tone={data.room?.nda_status === "Signed" ? "success" : data.room?.nda_status === "Received" ? "warn" : "muted"}>{data.room?.nda_status || "Pending"}</Pill></div>
              <div className="flex justify-between"><span className="text-ink-muted">LOI</span><Pill tone={data.room?.loi_status === "Signed" ? "success" : data.room?.loi_status === "Received" ? "warn" : "muted"}>{data.room?.loi_status || "Pending"}</Pill></div>
              <div className="flex justify-between"><span className="text-ink-muted">Term Sheet</span><Pill tone={data.room?.term_sheet_status === "Signed" ? "success" : data.room?.term_sheet_status === "Received" ? "warn" : "muted"}>{data.room?.term_sheet_status || "Pending"}</Pill></div>
              <div className="flex justify-between"><span className="text-ink-muted">Definitive Docs</span><Pill tone={data.room?.definitive_docs_status === "Signed" ? "success" : data.room?.definitive_docs_status === "Received" ? "warn" : "muted"}>{data.room?.definitive_docs_status || "Pending"}</Pill></div>
              {data.room?.stage === "Closed" && (
                <>
                  <div className="flex justify-between"><span className="text-ink-muted">Committed</span><span className="font-mono text-ink">₹{((data.room?.committed_amount_inr || 0) / 1e7).toFixed(2)} Cr</span></div>
                  <div className="flex justify-between"><span className="text-ink-muted">Success Fee</span><span className="font-mono text-ink">{data.room?.success_fee_pct}%</span></div>
                  <div className="flex justify-between"><span className="text-ink-muted">Invoice</span><span className="font-mono text-ink text-[10px]">{data.room?.invoice_id}</span></div>
                </>
              )}
            </div>
            {isAdmin && data.room?.stage !== "Closed" && (
              <>
                {!closing ? (
                  <Button variant="gold" className="mt-6 w-full" onClick={() => setClosing(true)} data-testid="dr-close-deal-open">
                    <CheckCircle size={14} className="inline mr-1" /> Close Deal &amp; Bill
                  </Button>
                ) : (
                  <div className="mt-6 p-3 rounded-md bg-gold/10 border border-gold/40 space-y-3" data-testid="dr-close-deal-form">
                    <div className="overline">Close &amp; Generate Invoice</div>
                    <Input label="Committed Amount (INR)" type="number" min="1" value={closeForm.committed_amount_inr} onChange={(e) => setCloseForm({ ...closeForm, committed_amount_inr: e.target.value })} testId="dr-close-amount" placeholder="50000000" />
                    <Input label="Success Fee %" type="number" min="0.1" max="20" step="0.1" value={closeForm.success_fee_pct} onChange={(e) => setCloseForm({ ...closeForm, success_fee_pct: e.target.value })} testId="dr-close-fee-pct" />
                    <Input label="Billing Email (optional)" type="email" value={closeForm.billed_to_email} onChange={(e) => setCloseForm({ ...closeForm, billed_to_email: e.target.value })} testId="dr-close-billing-email" placeholder="cfo@investor.com" />
                    <div className="flex gap-2">
                      <Button variant="gold" className="flex-1" onClick={closeDeal} data-testid="dr-close-confirm">Confirm &amp; Bill</Button>
                      <Button variant="ghost" className="flex-1" onClick={() => setClosing(false)} data-testid="dr-close-cancel">Cancel</Button>
                    </div>
                  </div>
                )}
              </>
            )}
            {data.room?.stage === "Closed" && (
              <Button variant="gold" className="mt-6 w-full" onClick={downloadInvoice} data-testid="dr-download-invoice">
                <Download size={14} className="inline mr-1" /> Download Invoice PDF
              </Button>
            )}
          </Card>

          <ClosingChecklistWidget
            roomId={id}
            viewerRole={user?.role}
            isAdmin={isAdmin}
            onChanged={load}
          />

          <Card testId="dr-notes">
            <div className="overline mb-3">Notes</div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(data.room?.notes || []).map((n) => (
                <div key={n.note_id} className="border border-white/10 p-2">
                  <div className="text-[11px] font-mono text-ink-muted uppercase tracking-wider">{n.role} · {n.by}</div>
                  <div className="text-sm text-ink-soft mt-1">{n.text}</div>
                </div>
              ))}
              {(data.room?.notes || []).length === 0 && <div className="text-ink-faint text-sm">No notes yet.</div>}
            </div>
            <div className="mt-3">
              <TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" testId="dr-note-input" />
              <Button onClick={addNote} className="mt-2 w-full" data-testid="dr-note-add">Add Note</Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );

  // Render inside dashboard shell if user is authenticated
  if (user) {
    return <DashboardShell title="Deal Room" subtitle={`ROOM · ${id.slice(0, 12)}`}>{content}</DashboardShell>;
  }
  return (
    <div className="min-h-screen bg-transparent text-ink">
      <PublicNavbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="overline mb-2">DEAL ROOM</div>
        <h1 className="font-display text-3xl mb-6">Deal Workspace</h1>
        {content}
      </div>
    </div>
  );
}
